/**
 * Production-only visibility hiding — CSS + router-guard approach (no
 * per-component conditionals). Env detection matches the existing
 * `AUTH_CLIENT_METADATA` / `resolveLinkedEmployeeEnv` convention
 * (`src/api/authClientMetadata.ts`, `src/api/services/iamUsers.ts`):
 * `MODE === 'production'` → production, everything else (dev, `vitest`
 * running MODE='test') → development.
 *
 * `PROD_HIDDEN_ROUTES` names are consumed two ways:
 *   1. `installAuthGuard` (`src/router/guards.ts`) redirects to
 *      `DEFAULT_LANDING_ROUTE` if a hidden route is reached directly by URL.
 *   2. `src/config/prod-hidden.css` hides the corresponding nav
 *      pill / sub-nav tab via its existing `data-testid`, scoped under
 *      the `html[data-env="production"]` attribute stamped once in
 *      `src/main.ts`.
 *
 * This only hides navigation entry points — it does not stop the route's
 * component from rendering if reached some other way, and it does not
 * remove anything from the shipped bundle. Content that must actually
 * change (different data, not just visibility) is handled in the
 * component itself, not here.
 */
export const IS_PRODUCTION = import.meta.env.MODE === 'production';

export const PROD_HIDDEN_ROUTES = [
  'home',
  'billing',
  'perform',
  'fieldops',
  'workmgmt',
  'insights',
  'pay-input-center',
  'pay-reports',
  'pay-configurations',
] as const;

export type ProdHiddenRoute = (typeof PROD_HIDDEN_ROUTES)[number];

export function isRouteHiddenInProd(routeName: string | null | undefined): boolean {
  if (!IS_PRODUCTION || !routeName) return false;
  return (PROD_HIDDEN_ROUTES as readonly string[]).includes(routeName);
}

/**
 * Home is hidden in production (see PROD_HIDDEN_ROUTES above), so it can't
 * be the universal default landing page. 'people' takes over as the
 * fallback destination in production only — dev/staging keep landing on
 * Home. Used for the '/' redirect, the 404 catch-all, the guard's
 * deny-access redirect, and the post-sign-in fallback (no `?redirect=`
 * carried over from a protected-route bounce).
 */
export const DEFAULT_LANDING_ROUTE: 'home' | 'people' = IS_PRODUCTION ? 'people' : 'home';
